// src/components/vault/documents/id-validation/IDDataConfirmationForm.tsx

import React from "react";
import {
  CheckCircle,
  Info,
  Loader2,
  Mail,
  MapPin,
  Pencil,
  Phone,
  User,
} from "lucide-react";
import { Button } from "../../../ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../../ui/alert-dialog";
import type { IDDataConfirmationFormProps } from "./types";
import { isSecondSurnameNotProvided } from "./idDataValidation";

interface DataFieldProps {
  label: string;
  value: string;
}

const DataField: React.FC<DataFieldProps> = ({ label, value }) => (
  <div className="space-y-1">
    <dt className="text-xs font-medium text-slate-500 uppercase tracking-wide">
      {label}
    </dt>
    <dd className="text-sm text-slate-900 font-medium">{value || "-"}</dd>
  </div>
);

export const IDDataConfirmationForm: React.FC<IDDataConfirmationFormProps> = ({
  data,
  onConfirm,
  onEdit,
  isSubmitting = false,
  requiresBdcAuthorization = false,
}) => {
  const noSecondSurname = isSecondSurnameNotProvided(data.apellidoMaterno);
  const fullName = [
    data.nombre,
    data.segundoNombre,
    data.apellidoPaterno,
    noSecondSurname ? "" : data.apellidoMaterno,
  ]
    .filter(Boolean)
    .join(" ");

  const fullAddress = [
    data.direccion.calle,
    data.direccion.numeroExterior && `#${data.direccion.numeroExterior}`,
    data.direccion.numeroInterior && `Int. ${data.direccion.numeroInterior}`,
  ]
    .filter(Boolean)
    .join(" ");

  const locationAddress = [
    data.direccion.colonia,
    data.direccion.codigoPostal && `C.P. ${data.direccion.codigoPostal}`,
  ]
    .filter(Boolean)
    .join(", ");

  const cityState = [data.direccion.municipio, data.direccion.estado]
    .filter(Boolean)
    .join(", ");
  const phone = data.phone_number
    ? `${data.dial_code} ${data.phone_number}`
    : "-";

  return (
    <div className="py-6 px-4 sm:px-6">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" strokeWidth={1.5} />
        </div>
        <h3 className="text-xl font-semibold text-slate-900 mb-2">
          Confirma tus datos
        </h3>
        <p className="text-sm text-slate-500 max-w-md mx-auto">
          Verificamos la informacion de tu INE. Confirma que tus datos son
          correctos y que el domicilio corresponde a donde vives actualmente.
        </p>
      </div>

      {/* Data sections */}
      <div className="space-y-6 max-w-lg mx-auto">
        {/* Personal info section */}
        <div className="bg-slate-50 rounded-xl p-5 border border-slate-100">
          <div className="flex items-center gap-2 mb-4">
            <User className="w-4 h-4 text-slate-500" />
            <h4 className="text-sm font-semibold text-slate-700">
              Datos personales
            </h4>
          </div>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <DataField label="Nombre completo" value={fullName} />
            {noSecondSurname && (
              <DataField label="Apellido materno" value="Sin segundo apellido" />
            )}
            <DataField label="CURP" value={data.curp} />
            <DataField label="RFC" value={data.rfc} />
            {requiresBdcAuthorization && (
              <DataField label="Fecha de nacimiento" value={data.fechaNacimiento} />
            )}
            {data.genero && <DataField label="Genero" value={data.genero} />}
          </dl>
        </div>

        {/* Address section */}
        <div className="bg-slate-50 rounded-xl p-5 border border-slate-100">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-4 h-4 text-slate-500" />
            <h4 className="text-sm font-semibold text-slate-700">
              Domicilio actual
            </h4>
          </div>
          <dl className="grid grid-cols-1 gap-3">
            <DataField label="Calle y numero" value={fullAddress} />
            <DataField label="Colonia y C.P." value={locationAddress} />
            <DataField label="Municipio y estado" value={cityState} />
          </dl>
          <div className="mt-4 flex gap-3 rounded-lg border border-blue-100 bg-blue-50 p-3 text-left">
            <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" />
            <p className="text-xs leading-relaxed text-blue-800">
              Usaremos esta direccion como tu domicilio actual para la
              solicitud. Si ya no vives ahi o hay algun error, selecciona
              "Corregir datos".
            </p>
          </div>
        </div>

        {requiresBdcAuthorization && (
          <div className="bg-slate-50 rounded-xl p-5 border border-slate-100">
            <div className="flex items-center gap-2 mb-4">
              <Mail className="w-4 h-4 text-slate-500" />
            <h4 className="text-sm font-semibold text-slate-700">
              Datos de contacto
            </h4>
          </div>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1 min-w-0">
              <dt className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                Correo electronico
              </dt>
              <dd className="break-all text-sm font-medium text-slate-900">
                {data.email || "-"}
              </dd>
            </div>
            <div className="space-y-1 min-w-0">
              <dt className="flex items-center gap-1 text-xs font-medium text-slate-500 uppercase tracking-wide">
                <Phone className="h-3 w-3" />
                Telefono
              </dt>
              <dd className="text-sm font-medium text-slate-900">{phone}</dd>
            </div>
          </dl>
            <p className="mt-4 text-xs leading-relaxed text-slate-500">
              El NIP inicial se enviará a este correo. El teléfono se usará
              únicamente si solicitas un reenvío por SMS.
            </p>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto mt-8">
        <Button
          variant="outline"
          className="flex-1 h-12 gap-2 text-base border-slate-200 hover:bg-slate-50"
          onClick={onEdit}
          disabled={isSubmitting}
        >
          <Pencil className="w-4 h-4 text-slate-500" />
          <span>Corregir datos</span>
        </Button>

        {requiresBdcAuthorization ? (
          <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              className="flex-1 h-12 gap-2 text-base bg-[#28397a] hover:bg-[#1e2d5f]"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              <span>
                {isSubmitting ? "Guardando..." : "Confirmar y Continuar"}
              </span>
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="max-h-[calc(100dvh-2rem)] max-w-md grid-rows-[minmax(0,1fr)_auto] overflow-hidden p-0 sm:rounded-2xl">
            <AlertDialogHeader className="min-h-0 space-y-4 overflow-y-auto overscroll-contain p-6 pb-4 text-left">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-50 text-[#28397a]">
                <CheckCircle className="h-5 w-5" aria-hidden="true" />
              </div>
              <div className="space-y-2">
                <AlertDialogTitle className="text-balance text-xl text-slate-900">
                  ¿Confirmas que toda tu información es correcta?
                </AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-4 text-pretty leading-6 text-slate-600">
                    <p>
                      Al continuar, confirmas que revisaste y que son correctos
                      los siguientes datos:
                    </p>
                    <ul className="space-y-3 rounded-xl bg-slate-50 p-4 text-left">
                      <li className="flex items-start gap-3">
                        <User
                          className="mt-0.5 h-4 w-4 shrink-0 text-slate-400"
                          aria-hidden="true"
                        />
                        <div className="min-w-0 flex-1 [overflow-wrap:anywhere]">
                          <p className="text-sm font-semibold text-slate-900">
                            Datos personales
                          </p>
                          <dl className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <div className="sm:col-span-2">
                              <DataField label="Nombre completo" value={fullName} />
                            </div>
                            <DataField label="CURP" value={data.curp} />
                            <DataField label="RFC" value={data.rfc} />
                            {requiresBdcAuthorization && (
                              <DataField label="Fecha de nacimiento" value={data.fechaNacimiento} />
                            )}
                            {data.genero && (
                              <DataField label="Género" value={data.genero} />
                            )}
                          </dl>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <MapPin
                          className="mt-0.5 h-4 w-4 shrink-0 text-slate-400"
                          aria-hidden="true"
                        />
                        <div className="min-w-0 flex-1 [overflow-wrap:anywhere]">
                          <p className="text-sm font-semibold text-slate-900">
                            Domicilio actual
                          </p>
                          <dl className="mt-3 grid grid-cols-1 gap-3">
                            <DataField label="Calle y número" value={fullAddress} />
                            <DataField label="Colonia y C.P." value={locationAddress} />
                            <DataField label="Municipio y estado" value={cityState} />
                          </dl>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <Mail
                          className="mt-0.5 h-4 w-4 shrink-0 text-slate-400"
                          aria-hidden="true"
                        />
                        <div className="min-w-0 flex-1 [overflow-wrap:anywhere]">
                          <p className="text-sm font-semibold text-slate-900">
                            Datos de contacto
                          </p>
                          <dl className="mt-3 grid grid-cols-1 gap-3">
                            <DataField label="Correo electrónico" value={data.email} />
                            <DataField label="Teléfono" value={phone} />
                          </dl>
                        </div>
                      </li>
                    </ul>
                    <div className="flex gap-3 rounded-lg border border-blue-100 bg-blue-50 p-3 text-left text-blue-800">
                      <Info
                        className="mt-0.5 h-4 w-4 shrink-0 text-blue-600"
                        aria-hidden="true"
                      />
                      <p className="text-sm leading-5">
                        El NIP inicial se enviará al correo confirmado. Tu
                        teléfono solo se usará si solicitas reenviarlo por SMS.
                      </p>
                    </div>
                    <p className="font-medium text-slate-700">
                      Guardaremos esta información para tu solicitud. Podrás
                      corregirla mientras no hayas iniciado la autorización de
                      Buró y el formulario no esté aprobado.
                    </p>
                  </div>
                </AlertDialogDescription>
              </div>
            </AlertDialogHeader>
            <AlertDialogFooter className="border-t border-slate-100 px-6 py-4 sm:space-x-3">
              <AlertDialogCancel className="h-11">
                Volver a revisar
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={onConfirm}
                disabled={isSubmitting}
                className="h-11 bg-[#28397a] hover:bg-[#1e2d5f]"
              >
                Sí, confirmar y continuar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
          </AlertDialog>
        ) : (
          <Button
            className="flex-1 h-12 gap-2 text-base bg-[#28397a] hover:bg-[#1e2d5f]"
            onClick={onConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4" />
            )}
            <span>
              {isSubmitting ? "Guardando..." : "Confirmar y Continuar"}
            </span>
          </Button>
        )}
      </div>

      {/* Helper text */}
      <p className="text-xs text-slate-400 mt-6 text-center max-w-sm mx-auto">
        Si algún dato es incorrecto, selecciona "Corregir datos" para editarlo
        manualmente.
      </p>
    </div>
  );
};
